import { PBRMaterial } from './PBRMaterial';
import { InterpolatedProperty } from '../properties/InterpolatedProperty';
import { SimpleSettingsProperty } from "../properties/SimpleSettingsProperty";
import { Color } from '../types';
import { SceneSettingsData } from '../core/SceneSettingsData';
import { AppSettingsData } from '../core/AppSettingsData';
import { POIData } from '../poi/POIData';
import { TextureBacksideMode } from '../core/AppSettingsData';
import * as THREE from 'three';
import { Logger } from '../core/Logger';

export type TextureWrap = 'repeat' | 'clampToEdge' | 'mirroredRepeat';
export type TextureFilter = 'nearest' | 'linear' | 'nearestMipmapNearest' | 'linearMipmapNearest' | 'nearestMipmapLinear' | 'linearMipmapLinear';

/** Normalize editor/legacy URI shorthands without corrupting data: URLs. */
export function normalizeTextureUri(uri: string | null | undefined): string {
    let normalized = uri ?? '';
    try {
        const trimmed = normalized.trim();
        normalized = trimmed.replace(/^@+/, '');
        if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
            try {
                const parsed = JSON.parse(trimmed);
                if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'string') {
                    normalized = String(parsed[0]);
                }
            } catch {
                // Keep the original value when this is not valid JSON.
            }
        }
        if ((normalized.startsWith('"') && normalized.endsWith('"')) || (normalized.startsWith('\'') && normalized.endsWith('\''))) {
            normalized = normalized.slice(1, -1);
        }
        if (normalized.includes(';') && !normalized.startsWith('data:')) {
            normalized = normalized.split(';')[0].trim();
        }
    } catch {
        // Preserve the best-effort value for malformed legacy inputs.
    }
    return normalized;
}



export class TexturedMaterial extends PBRMaterial {
    public readonly uri: SimpleSettingsProperty<string>;
    public readonly repeatX: InterpolatedProperty<number>;
    public readonly repeatY: InterpolatedProperty<number>;
    public readonly offsetX: InterpolatedProperty<number>;
    public readonly offsetY: InterpolatedProperty<number>;
    public readonly rotation: InterpolatedProperty<number>;
    public readonly wrapS: SimpleSettingsProperty<TextureWrap>;
    public readonly wrapT: SimpleSettingsProperty<TextureWrap>;
    public readonly magFilter: SimpleSettingsProperty<TextureFilter>;
    public readonly minFilter: SimpleSettingsProperty<TextureFilter>;
    public readonly flipY: SimpleSettingsProperty<boolean>;
    public readonly premultiplyAlpha: SimpleSettingsProperty<boolean>;
    public readonly lodBias: InterpolatedProperty<number>;
    
    // The aspect ratio of the loaded texture (width / height)
    public readonly aspectRatio: SimpleSettingsProperty<number>;
    
    // Store the original texture aspect ratio (before POI override)
    public originalTextureAspectRatio: number = 1.0;

    protected _texture: any = null;
    public isLoading: boolean = false;
    public error: string | null = null;

    constructor(
        type: 'textured' | 'image' | 'video' | 'canvas',
        name: string = 'TexturedMaterial',
        poiData: POIData
    ) {
        super(name, poiData);
        
        const groupName = 'Texture';
        this.uri = new SimpleSettingsProperty('Texture URL', null, '', groupName);
        this.repeatX = new InterpolatedProperty('Repeat X', null, 1.0, 0.2, groupName).withBounds(0, 10, 0.1);
        this.repeatY = new InterpolatedProperty('Repeat Y', null, 1.0, 0.2, groupName).withBounds(0, 10, 0.1);
        this.offsetX = new InterpolatedProperty('Offset X', null, 0.0, 0.2, groupName).withBounds(-1, 1, 0.05);
        this.offsetY = new InterpolatedProperty('Offset Y', null, 0.0, 0.2, groupName).withBounds(-1, 1, 0.05);
        this.rotation = new InterpolatedProperty('Rotation', null, 0.0, 0.2, groupName).withBounds(-360, 360, 1);
        this.wrapS = new SimpleSettingsProperty<TextureWrap>('Wrap S', null, 'repeat', groupName);
        this.wrapT = new SimpleSettingsProperty<TextureWrap>('Wrap T', null, 'repeat', groupName);
        this.magFilter = new SimpleSettingsProperty<TextureFilter>('Mag Filter', null, 'linear', groupName);
        this.minFilter = new SimpleSettingsProperty<TextureFilter>('Min Filter', null, 'linearMipmapLinear', groupName);
        this.flipY = new SimpleSettingsProperty('Flip Y', null, true, groupName);
        this.premultiplyAlpha = new SimpleSettingsProperty('Premultiply Alpha', null, false, groupName);
        this.aspectRatio = new SimpleSettingsProperty('Aspect Ratio', null, 1.0, groupName);
        this.lodBias = new InterpolatedProperty('LOD Bias', null, 0.0, 0.1, groupName).withBounds(-2, 2, 0.05);

        // Note: POIData.uri is now an array, so we don't link automatically
        // MaterialFactory will call setUri() after creation
        
        // Set up aspect ratio parent relationship
        this.setupAspectRatioHierarchy(poiData);
    }

    /**
     * Set up aspect ratio hierarchy - POI aspectRatio overrides automatic texture detection
     */
    private setupAspectRatioHierarchy(poiData: POIData): void {
        // Only link if POI has a manual aspect ratio set (> 0)
        if (poiData.aspectRatio && (poiData.aspectRatio.value ?? 0) > 0) {
            this.aspectRatio.addParent(poiData.aspectRatio);
        }
        
        // Listen for changes to re-evaluate the hierarchy
        poiData.aspectRatio?.on((newValue) => {
            if ((newValue ?? 0) > 0) {
                // Switch to manual aspect ratio
                this.aspectRatio.addParent(poiData.aspectRatio!);
            } else {
                // Switch back to automatic detection
                this.aspectRatio.removeParent(poiData.aspectRatio!);
            }
        });
    }

    /**
     * Set the URI for this material (used by MaterialFactory for multi-URI support)
     */
    public setUri(uri: string): void {
        //console.log(`🔧 [DEBUG] ${this.name}: setUri('${uri}') - was: '${this.uri.value}'`);
        this.uri.value = normalizeTextureUri(uri);
    }

    protected setupPropertyListeners(): void {
        const update = () => this.updateTextureProperties();

        this.uri.on(url => {
            //console.log(`🔗 [DEBUG] ${this.name}: URI changed to '${url}' - triggering loadTexture`);
            if (url) {
                this.loadTexture(url);
            } else {
                //console.log(`🗑️ [DEBUG] ${this.name}: URI cleared - disposing texture`);
                this.disposeTexture();
            }
        });

        this.repeatX.on(update);
        this.repeatY.on(update);
        this.offsetX.on(update);
        this.offsetY.on(update);
        this.rotation.on(update);
        this.wrapS.on(update);
        this.wrapT.on(update);
        this.magFilter.on(update);
        this.minFilter.on(update);
        this.flipY.on(update);
        this.premultiplyAlpha.on(update);
    }
    
    getThreeMaterial(): THREE.Material {
        return super.getThreeMaterial();
    }
    
    protected updateTextureProperties(): void {
        // To be implemented by subclasses like ImageMaterial
    }

    protected loadTexture(url: string): void {
        // To be implemented by subclasses
    }

    protected disposeTexture(): void {
        // To be implemented by subclasses
    }
    
    /**
     * Determines if backside rendering should be enabled based on global settings and texture properties
     */
    protected shouldRenderBackside(): boolean {
        const mode = AppSettingsData.Instance.textureBacksideMode.value;
        const uri = this.uri.value?.toLowerCase() || '';
        
        switch (mode) {
            case TextureBacksideMode.NEVER:
                return false;
                
            case TextureBacksideMode.ALWAYS:
                return true;
                
            case TextureBacksideMode.AUTO_PNG:
                // Enable backside rendering for PNG files
                return uri.endsWith('.png') || (this.renderBackface.value ?? false);
                
            case TextureBacksideMode.AUTO_TRANSPARENT:
                // Enable backside rendering for transparent textures (detected at runtime)
                return this.hasTransparency() || (this.renderBackface.value ?? false);
                
            default:
                return this.renderBackface.value ?? false;
        }
    }
    
    /**
     * Checks if the loaded texture has transparency
     * This is a base implementation - subclasses can override for more specific detection
     */
    protected hasTransparency(): boolean {
        if (!this._texture) return false;
        
        // Check texture format for alpha channel
        const hasAlphaFormat = this._texture.format === THREE.RGBAFormat || 
                              this._texture.format === THREE.RGBA_S3TC_DXT5_Format ||
                              this._texture.format === THREE.RGBA_ASTC_4x4_Format ||
                              this._texture.format === THREE.RGBA_BPTC_Format ||
                              this._texture.format === THREE.RGBA_ETC2_EAC_Format;
        
        return hasAlphaFormat;
    }
    
    /**
     * Gets the appropriate Three.js side constant based on global settings
     * Overrides the base class method to include texture-specific logic
     */
    protected override getAppropriateThreeSide(): THREE.Side {
        const shouldRenderBackside = this.shouldRenderBackside();
        return shouldRenderBackside ? THREE.DoubleSide : THREE.FrontSide;
    }
    
    /**
     * Gets the appropriate Three.js side constant based on global settings
     * @deprecated Use getAppropriateThreeSide() instead
     */
    protected getThreeSide(appSettings?: any): THREE.Side {
        return this.getAppropriateThreeSide();
    }
    


    /**
     * 🎯 CLEAN: Setup this material to use primary roughness/metalness properties
     */
    public setupAsPrimary(poiData: any): void {
        this.metalness.addParent(poiData.primaryMetalness);
        this.roughness.addParent(poiData.primaryRoughness);
    }

    /**
     * 🎯 CLEAN: Setup this material to use secondary roughness/metalness properties  
     */
    public setupAsSecondary(poiData: any): void {
        this.metalness.addParent(poiData.secondaryMetalness);
        this.roughness.addParent(poiData.secondaryRoughness);
    }
    
    // Set texture repeat
    setRepeat(x: number, y: number = x): void {
        this.repeatX.value = x;
        this.repeatY.value = y;
    }
    
    // Set texture offset
    setOffset(x: number, y: number = x): void {
        this.offsetX.value = x;
        this.offsetY.value = y;
    }
    
    // Set texture wrapping
    setWrap(wrapS: TextureWrap, wrapT: TextureWrap = wrapS): void {
        this.wrapS.value = wrapS;
        this.wrapT.value = wrapT;
    }
    
    // Set texture filtering
    setFilter(magFilter: TextureFilter, minFilter: TextureFilter = magFilter): void {
        this.magFilter.value = magFilter;
        this.minFilter.value = minFilter;
    }
    
    // Dispose texture resources
    dispose(): void {
        this.disposeTexture();
    }
    
    // Factory method
    static create(params: any = {}): TexturedMaterial {
        throw new Error('TexturedMaterial is abstract. Use ImageMaterial, VideoMaterial, or CanvasMaterial instead.');
    }
}
